import api from './api';

// Função auxiliar para formatar data/hora para exibição
function formatDateTime(dateTimeString) {
    if (!dateTimeString) return '-';
    try {
        const date = new Date(dateTimeString);
        if (isNaN(date.getTime())) return '-';
        
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return '-';
    }
}

// Função auxiliar para formatar duração
function formatDuration(minutes) {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${mins}min`;
}

// Cores padrão para agendamentos
const appointmentColors = {
    SCHEDULED: '#007bff',
    COMPLETED: '#28a745',
    CANCELLED: '#dc3545'
};

// Status traduzidos
const statusLabels = {
    SCHEDULED: 'Agendado',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado'
};

const appointmentsService = {
    // Listar agendamentos
    async getAll(params = {}) {
        try {
            let url = '/appointment/';
            const queryParams = new URLSearchParams();
            
            // Parâmetros de filtro
            if (params.personId) queryParams.append('personId', params.personId);
            if (params.status) queryParams.append('status', params.status);
            if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
            if (params.startDate) queryParams.append('start', params.startDate);
            if (params.endDate) queryParams.append('end', params.endDate);
            if (params.page) queryParams.append('page', params.page);
            if (params.pageSize) queryParams.append('pageSize', params.pageSize);
            
            if (queryParams.toString()) {
                url += '?' + queryParams.toString();
            }
            
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            console.error('Erro ao listar agendamentos:', error);
            throw new Error('Não foi possível carregar os agendamentos.');
        }
    },

    // Buscar agendamento por ID
    async getById(id) {
        try {
            const response = await api.get(`/appointment/${id}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar agendamento:', error);
            throw new Error('Não foi possível carregar o agendamento.');
        }
    },

    // Criar novo agendamento
    async create(appointmentData) {
        try {
            const response = await api.post('/appointment/', appointmentData);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar agendamento:', error);
            throw new Error('Não foi possível criar o agendamento. Verifique os dados e tente novamente.');
        }
    },

    // Atualizar agendamento
    async update(id, appointmentData) {
        try {
            const response = await api.put(`/appointment/${id}`, appointmentData);
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar agendamento:', error);
            throw new Error('Não foi possível atualizar o agendamento. Verifique os dados e tente novamente.');
        }
    },

    // Excluir agendamento
    async delete(id) {
        try {
            await api.delete(`/appointment/${id}`);
            return { success: true };
        } catch (error) {
            console.error('Erro ao excluir agendamento:', error);
            throw new Error('Não foi possível excluir o agendamento.');
        }
    },

    // Criar agendamentos recorrentes
    async createRecurring(recurringData) {
        try {
            const response = await api.post('/appointment/recurring', recurringData);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar agendamentos recorrentes:', error);
            throw new Error('Não foi possível criar os agendamentos recorrentes. Verifique os dados e tente novamente.');
        }
    },

    // Listar agendamentos por paciente
    async getByPatient(patientId, params = {}) {
        try {
            let url = `/api/appointment/person/${patientId}`;
            const queryParams = new URLSearchParams();
            
            if (params.status) queryParams.append('status', params.status);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            
            if (queryParams.toString()) {
                url += '?' + queryParams.toString();
            }
            
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            console.error('Erro ao listar agendamentos do paciente:', error);
            throw new Error('Não foi possível carregar os agendamentos do paciente.');
        }
    },

    // Atualizar status de pagamento
    async updatePayment(id, paymentData) {
        try {
            const response = await api.put(`/appointment/${id}/payment`, paymentData);
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar pagamento:', error);
            throw new Error('Não foi possível atualizar o status de pagamento.');
        }
    },


    // Atualizar valor e/ou cor de todos os agendamentos de uma recorrência
    async updateRecurrenceFields(recurrenceId, { color, consultationValue, notes, date, time, afterDate }) {
        try {
            const payload = {};
            if (color !== undefined) payload.color = color;
            if (consultationValue !== undefined) payload.consultationValue = consultationValue;
            if (notes !== undefined) payload.notes = notes;
            if (date !== undefined) payload.date = date;
            if (time !== undefined) payload.time = time;
            if (afterDate) payload.afterDate = afterDate;
            
            const response = await api.put(`/appointment/recurrence/${recurrenceId}/fields`, payload);
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar campos da recorrência:', error);
            console.error('Erro detalhado:', error.response?.data);
            throw new Error('Não foi possível atualizar os campos da recorrência.');
        }
    },

    // Deletar agendamentos recorrentes após uma data
    async deleteRecurrenceAfterDate(recurrenceId, afterDate) {
        try {
            const response = await api.delete(`/appointment/recurrence/${recurrenceId}/after`, { afterDate });
            return response.data;
        } catch (error) {
            console.error('Erro ao deletar agendamentos recorrentes:', error);
            console.error('Erro detalhado:', error.response?.data);
            throw new Error('Não foi possível deletar os agendamentos recorrentes.');
        }
    },

    // Converter agendamentos para formato do FullCalendar
    formatForCalendar(appointments, patients = []) {
        if (!Array.isArray(appointments)) return [];
        
        return appointments.map(appointment => {
            // Log para debug
            console.log('=== FORMATANDO APPOINTMENT ===');
            console.log('Appointment original:', appointment);
            
            // Buscar nome do paciente
            const patient = patients.find(p => p.id === appointment.personId);
            const patientName = patient ? 
                `${patient.firstName || ''} ${patient.lastName || ''}`.trim() : 
                'Paciente não encontrado';
            
            // Verificar se tem os campos novos (start_date, end_date, all_day) ou antigos (dateTime, durationMinutes)
            let start, end, isAllDay;
            
            if (appointment.start_date && appointment.end_date) {
                // Backend retorna start_date e end_date
                start = new Date(appointment.start_date);
                end = new Date(appointment.end_date);
                isAllDay = appointment.all_day === true;
            } else if (appointment.dateTime) {
                // Fallback: formato antigo com dateTime e durationMinutes
                start = new Date(appointment.dateTime);
                end = new Date(start.getTime() + (appointment.durationMinutes || 60) * 60000);
                isAllDay = (appointment.durationMinutes || 60) >= 1440;
            } else {
                console.error('Appointment sem data válida:', appointment);
                return null;
            }
            
            const event = {
                id: appointment.id,
                title: patientName,
                backgroundColor: appointment.color || appointmentColors[appointment.status] || appointmentColors.SCHEDULED,
                borderColor: appointment.color || appointmentColors[appointment.status] || appointmentColors.SCHEDULED,
                extendedProps: {
                    appointmentId: appointment.id,
                    personId: appointment.personId,
                    patientName: patientName,
                    status: appointment.status,
                    statusLabel: statusLabels[appointment.status] || appointment.status,
                    duration: appointment.durationMinutes,
                    notes: appointment.notes,
                    recurrenceId: appointment.recurrenceId,
                    isPaid: appointment.isPaid,
                    paymentMethod: appointment.paymentMethod,
                    paymentDate: appointment.paymentDate,
                    consultationValue: appointment.consultationValue
                }
            };
            
            // Para eventos all-day, usar apenas a data sem hora
            if (isAllDay) {
                event.allDay = true;
                // Usar apenas a data no formato YYYY-MM-DD
                // Extrair a data do start ignorando timezone para eventos all-day
                const year = start.getFullYear();
                const month = String(start.getMonth() + 1).padStart(2, '0');
                const day = String(start.getDate()).padStart(2, '0');
                event.start = `${year}-${month}-${day}`;
            } else {
                event.start = start.toISOString();
                event.end = end.toISOString();
            }
            
            console.log('Event formatado:', event);
            console.log('========================');
            
            return event;
        }).filter(event => event !== null);
    },

    // Construir dados de agendamento a partir de formulário
    buildAppointmentData(formData) {
        // Se recebeu dateTime (formato ISO), extrair date e time
        let date = formData.date;
        let time = formData.time;
        
        if (formData.dateTime && !date && !time) {
            // Converter de ISO para YYYY-MM-DD e HH:MM
            // FullCalendar com timezone nomeado usa "fake UTC": usamos getUTC* para extrair
            // o horário de parede correto, independente do timezone do navegador.
            const dateObj = new Date(formData.dateTime);
            date = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD (via UTC)
            const hours = String(dateObj.getUTCHours()).padStart(2, '0');
            const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0');
            time = `${hours}:${minutes}`; // HH:MM
        }
        
        return {
            personId: formData.personId,
            date: date,
            time: time,
            duration: parseInt(formData.durationMinutes) || 60,
            status: formData.status || 'SCHEDULED',
            notes: formData.notes || '',
            color: formData.color || appointmentColors.SCHEDULED,
            consultationValue: typeof formData.consultationValue === 'number' ? formData.consultationValue : 0,
            paymentStatus: 'PENDING',
            paymentValue: 0
        };
    },

    // Construir dados de agendamento recorrente
    buildRecurringData(formData) {
        // Converter data de dd/mm/yyyy para YYYY-MM-DD
        let startDate = formData.startDate;
        if (startDate && startDate.includes('/')) {
            const [day, month, year] = startDate.split('/');
            startDate = `${year}-${month}-${day}`;
        }
        
        return {
            personId: formData.personId,
            startDate: startDate,
            startTime: formData.startTime,
            duration: parseInt(formData.durationMinutes) || 60,
            frequency: formData.frequency || 'WEEKLY',
            recurDuration: 'INDEFINITE',
            notes: formData.notes || '',
            color: formData.color || appointmentColors.SCHEDULED,
            consultationValue: typeof formData.consultationValue === 'number' ? formData.consultationValue : 0
        };
    },

    // Formatadores e constantes
    formatters: {
        formatDateTime,
        formatDuration
    },
    
    constants: {
        appointmentColors,
        statusLabels,
        
        frequencies: {
            DAILY: 'Diário',
            WEEKLY: 'Semanal',
            BIWEEKLY: 'Quinzenal',
            MONTHLY: 'Mensal'
        },
        
        durations: {
            ONE_MONTH: '1 Mês',
            THREE_MONTHS: '3 Meses',
            SIX_MONTHS: '6 Meses',
            UNTIL_YEAR_END: 'Até o fim do ano'
        }
    }
};

export default appointmentsService;